import { useEffect, useState, useMemo } from "react"
import Datatable from "../../Data/TablesData/Datatable"
import { useDatatableCtx } from "../../components/Datatable/DatatableContext"
import { useModalCtx } from "../../providers/ModalProvider"
import Modal from "../../components/Modal"
import { Grid, MenuItem, TextField } from "@mui/material"
import Kanban from "./Kanban/components/kanban"
import { makeDragAndDropData, makeDragAndDropDataForUpdateAPI } from "./Kanban/mockData"
import UpdateKanBan from "./Kanban/components/kanban/UpdateKanBan"
import { computeColumns } from "../../utils"
import { useLocalization } from "../../providers/LocalizationProvider"
import { useFormat } from "../../context/FormatContext"
import SimpleBackdropLoader from "../BackdropLoading/BackdropLoading"
import { FetchHelper } from "../../utils/fetchHelper"
import { showError, showSnackbar, showSuccess } from "../../utils/snackbar"

export const BASE_URL = process.env.REACT_APP_API_URL

export const AssetMappingDatatable = {
    meta: {
        capacity_total: {
            showDefault: false,
        },
        created_at: {
            // hide: true,
            show: false,
        },
        updated_at: {
            show: false,
        },
        zone_id: {
            show: false,
        },
        id: {
            show: false,
        },
        uuid: {
            show: false,
        },
    },
}

const AssetMapping = () => {
    const modalContext = useModalCtx()
    const { setModalStatus, setRowData, setColumnMetaData } = useDatatableCtx()
    const [isEditMode, setEditMode] = useState(false)
    const [zoneMapTableData, setZoneMapTableData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const { modalStatus: formModalStatus, modalStatusHandler: handleFormModalStatus } = modalContext
    const { voiceiiTags } = useLocalization()
    const { formatDate } = useFormat()

    const columns = useMemo(() =>
        computeColumns({
            data: zoneMapTableData,
            meta: AssetMappingDatatable.meta,
            action: ({ row, meta }) => {
                setRowData(row)
                setColumnMetaData(meta)
                setModalStatus(true)
            },
            formatDate,
        }),
    )
    const data = useMemo(() => zoneMapTableData, [zoneMapTableData])

    const [assetType, setAssetType] = useState([])
    const [zoneId, setZoneId] = useState([])
    const [zoneLabel, setZoneLabel] = useState([])
    const [selectedAssetType, setSelectedAssetType] = useState("")
    const [formattedDragAndDropData, setFormattedDragAndDropData] = useState([])

    const [updatedDragDropData, setUpdatedDragDropData] = useState([])
    const [uuidForUpdateApi, setUuidForUpdateApi] = useState("")
    const [updateZoneIds, setUpdateZoneIds] = useState([])
    const [updateZoneLabels, setUpdateZoneLabels] = useState([])
    const [editAssetType, setEditAssetType] = useState("")
    const [formatDataToupdate, setFormatDataToupdate] = useState([])

    const [istableLoading, setIstableLoading] = useState(true)

    useEffect(() => {}, [])
    // drag and drop data
    const fetchZonesData = async () => {
        try {
            const resData = await FetchHelper.post("api/v1/data", {
                page_name: "zones",
                uuid: "",
            })

            if (resData?.data?.status) {
                const formattedData = makeDragAndDropData(resData?.data?.data)
                setFormattedDragAndDropData(formattedData)
                // This is to use for update API as this data is already formatted no need to run for loop again
                setFormatDataToupdate(resData?.data?.data)
            }
        } catch (error) {
            console.warn(error, "Error")
        }
    }

    useEffect(() => {
        fetchZonesData()
    }, [])

    // data to show on table
    const fetchAssetZonesData = async () => {
        try {
            const apiData = await FetchHelper.post("api/v1/data", {
                page_name: "asset_zones_map",
                uuid: "",
            })
            if (apiData?.data?.status) {
                setZoneMapTableData(apiData?.data?.data)
                setIstableLoading(false)
            } else {
                setIstableLoading(false)
            }
        } catch (error) {
            console.warn(error, "Error")
            setIstableLoading(false)
        } finally {
            setIstableLoading(false)
        }
    }

    // add asset data map
    const addAssetZoneMap = async () => {
        const apiData = {
            zone_id: zoneId,
            zone_label: zoneLabel,
            asset_uuid: selectedAssetType || "",
        }

        try {
            setIsLoading(true)
            const resData = await FetchHelper.post("api/v1/asset_zones_map", apiData)
            if (resData?.data?.status) {
                handleFormModalStatus(false)
                fetchAssetZonesData()
                showSnackbar(voiceiiTags(resData?.data?.message))
            } else {
                showError(voiceiiTags(resData?.data?.message))
                fetchAssetZonesData()
            }
        } catch (error) {
            showError(voiceiiTags(error.response?.data?.message))
            console.warn(error, "Error")
        } finally {
            setIsLoading(false)
        }
    }

    // update api
    const updateAssetZoneMapData = async () => {
        const apiData = {
            zone_id: updateZoneIds,
            zone_label: updateZoneLabels,
        }

        try {
            setIsLoading(true)
            const resData = await FetchHelper.post(
                `api/v1/asset_zones_map/${uuidForUpdateApi}`,
                apiData,
            )
            if (resData?.data?.status) {
                setUpdateZoneIds([])
                setUpdateZoneLabels([])
                fetchAssetZonesData()
                handleFormModalStatus(false)
                showSuccess(voiceiiTags(resData.data.message))
            } else {
                setUpdateZoneIds([])
                setUpdateZoneLabels([])
                showError(voiceiiTags(resData.data.message))
            }
        } catch (error) {
            console.warn(error, "Error")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchAssetZonesData()
    }, [])

    // data to show on dropdown
    const fetchAssetTypeData = async () => {
        try {
            const apiData = await FetchHelper.get("api/v1/asset?only_active=true")
            if (apiData?.data?.status) {
                setAssetType(apiData?.data?.data?.assets)
            }
        } catch (error) {
            console.warn(error, "Error")
        }
    }

    useEffect(() => {
        fetchAssetTypeData()
    }, [])

    return (
        <div className="e-asset-mapping-container">
            <Datatable
                filteredResult={data}
                datatableTitle={voiceiiTags("Asset Type Zone Mapping")}
                columns={columns}
                data={data}
                loading={istableLoading}
                actions={[{ name: "Edit", type: "edit", buttonType: "icon" }]}
                addActionName={voiceiiTags("New Asset Mapping")}
                columnConfigKey={"asset_type_zone_mapping_columns_config"}
                addAction={() => {
                    handleFormModalStatus(true)
                    setEditMode(false)
                }}
                refreshAction={() => {
                    fetchAssetZonesData()
                }}
                onActionClick={(event, { type, data }) => {
                    if (type == "edit") {
                        const updatedformatData = makeDragAndDropDataForUpdateAPI(
                            formatDataToupdate,
                            data?.values,
                        )

                        setUuidForUpdateApi(data?.values?.uuid)
                        setEditAssetType(data?.values?.asset_type)
                        setUpdatedDragDropData(updatedformatData)
                        setEditMode(true)
                        handleFormModalStatus(true)
                    }
                }}
                showMapViewButton={false}
            />

            <Modal
                modalHeading={
                    isEditMode
                        ? voiceiiTags("Update Asset Type Zone Mapping")
                        : voiceiiTags("Asset Type Zone Mapping")
                }
                fullWidth
                maxWidth="md"
                status={formModalStatus}
                onClose={() => {
                    setZoneId([])
                    setZoneLabel([])
                    handleFormModalStatus(false)
                    setFormattedDragAndDropData([])
                    fetchZonesData()
                }}
                onSave={() => {
                    isEditMode ? updateAssetZoneMapData() : addAssetZoneMap()
                }}
                disableSaveBtn={
                    (isEditMode && (updateZoneIds.length === 0 || updateZoneLabels.length === 0)) ||
                    isLoading
                }
            >
                <SimpleBackdropLoader showLoader={isLoading} />
                <br />
                {isEditMode ? (
                    <Grid>
                        <Grid item xs={6}>
                            <TextField
                                value={editAssetType}
                                fullWidth
                                select
                                label={voiceiiTags("Asset Type")}
                                name="select_wrapper_name"
                                className="text-capitalize"
                                disabled
                            >
                                <MenuItem className="text-capitalize" value={editAssetType}>
                                    {editAssetType}
                                </MenuItem>
                            </TextField>
                            <Grid className="e-asset-mapping-kanban-wrapper">
                                <UpdateKanBan
                                    updatedDragDropData={updatedDragDropData}
                                    setUpdateZoneIds={setUpdateZoneIds}
                                    setUpdateZoneLabels={setUpdateZoneLabels}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid>
                        <Grid item xs={6}>
                            <TextField
                                className="text-capitalize"
                                fullWidth
                                select
                                value={selectedAssetType}
                                label={voiceiiTags("Asset Type")}
                                name="select_wrapper_name"
                                onChange={(e) => {
                                    setSelectedAssetType(e.target.value)
                                }}
                            >
                                {assetType &&
                                    assetType?.map((dynamicAsset) => (
                                        <MenuItem
                                            className="text-capitalize"
                                            key={dynamicAsset.uuid}
                                            value={dynamicAsset.uuid}
                                        >
                                            {voiceiiTags(dynamicAsset?.name)}
                                        </MenuItem>
                                    ))}
                            </TextField>
                            <Grid className="e-asset-mapping-kanban-wrapper">
                                <Kanban
                                    formattedDragAndDropData={formattedDragAndDropData}
                                    setZoneLabel={setZoneLabel}
                                    setZoneId={setZoneId}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Modal>
        </div>
    )
}

export default AssetMapping
